import React from "react"
import { ProductCarousel } from "@app/scripts/components/product-carousel"
import { useSearchspringRecommendResults } from "@app/scripts/hooks/use-searchspring-recommend-results"
import { SearchspringRecommendProvider } from "@app/scripts/providers/searchspring"

/**
 * Configuration for the collection grid recommendation carousel (#1822).
 * Built in Liquid from the `recommendation_carousel` block on the
 * main-collection-product-grid section and passed through collectionData.
 */
export type RecommendationCarouselConfig = {
  /** Heading rendered above the carousel. Blank renders no heading. */
  heading: string
  /** Searchspring profile tag, e.g. "collection-page". */
  profileTag: string
  /** Insert the carousel after this many product results (banners excluded). */
  position: number
  /** Number of products requested from Searchspring (`limits`). */
  limit: number
  /** Collection handle sent as `filter.collection_handle`. */
  filterCollection: string
}

type Props = {
  config: RecommendationCarouselConfig
}

/**
 * Minimum number of recommendations needed before the row renders.
 * 4 matches the display threshold configured on the existing Searchspring
 * recommendation profiles, and fills one desktop grid row.
 */
const MIN_RESULTS = 4

/**
 * Full-width recommendation row inside the Searchspring collection grid.
 * Renders nothing until results load, and nothing at all when fewer than
 * MIN_RESULTS products come back, so the grid never shows an empty row.
 *
 * Note: the provider reports a recommendations render beacon as soon as a
 * response arrives, including responses below MIN_RESULTS that render
 * nothing, so Searchspring render counts for this profile can run slightly
 * high. Accepted for now; the same applies to the existing tab carousels.
 */
export function CollectionRecommendationCarousel({ config }: Props) {
  return (
    <SearchspringRecommendProvider
      category="products"
      profile={config.profileTag}
      limit={config.limit}
      filterCollection={config.filterCollection}
    >
      <RecommendationRow heading={config.heading} />
    </SearchspringRecommendProvider>
  )
}

function RecommendationRow({ heading }: { heading: string }) {
  const results = useSearchspringRecommendResults()

  // Also covers "not loaded yet": the hook returns [] until the response arrives.
  if (results.length < MIN_RESULTS) return null

  // The modifier reserves space for the desktop arrows when there is no heading line to sit beside.
  const className = heading
    ? "ss-product-grid__recommendations"
    : "ss-product-grid__recommendations ss-product-grid__recommendations--no-heading"

  return (
    <div className={className}>
      {heading && (
        <h2 className="ss-product-grid__recommendations-heading">{heading}</h2>
      )}
      <ProductCarousel visible={true} position="relative" />
    </div>
  )
}
